import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http'; // Modulo dee Angular http
import { Router } from '@angular/router';
import { OrdenComercio } from '../models/OrdenComercio';
import { Ubicacion } from '../models/Ubicacion';
import { Comercio } from '../models/Comercio';
import { catchError, firstValueFrom, map, Observable, of, switchMap, throwError } from 'rxjs';
import { ProductoOrdenComercio } from '../models/ProductoOrdenComercio';
import { VademecumOrdenComercio } from '../models/VademecumOrdenComercio';
import { Vademecum } from '../models/Vademecum';
import { NotificacionService } from './notificacion.service';
import { EstadoOrden } from '../models/EstadoOrden';
import { API_URI } from 'src/environments/environment';
import { CalificacionOrdenComercio } from '../models/CalificacionOrdenComercio';

// Fila de la lista admin ("Órdenes de Comercio" en More) — ver
// ordenComercioController.buscarAdmin en el backend.
export interface OrdenComercioAdminRow {
  id: number;
  id_cliente: string;
  id_comercio: string;
  id_estado_orden: number;
  d_date_create: string | Date | null;
  n_precio_envio?: number | null;
  estadoOrden: string;
  nombreCliente: string;
  nombreComercio: string;
}

export interface OrdenComercioAdminResponse {
  rows: OrdenComercioAdminRow[];
  total: number;
  page: number;
  limit: number;
}

@Injectable({
  providedIn: 'root'
})
export class OrderComercioService {
  private readonly _notificacionService = inject(NotificacionService);
  private readonly API_URI: string = API_URI + 'app';
  vademecums: Vademecum[] = [];
  estadosOrdenes: EstadoOrden[] = [];

  ordenComercio: OrdenComercio = {
    id: null,
    id_cliente: null,
    id_comercio: null,
    n_latitud: null,
    n_longitud: null,
    x_zona: null,
    x_img_receta: null,
    b_retiro_local: false,
    b_envio_domicilio: false,
    x_envio_direccion: '',
    b_pago_efectivo: false,
    b_pago_mercadopago: false
  };
  comercio?: Comercio;
  ubicacion?: Ubicacion;
  productosOrdenComercio: ProductoOrdenComercio[] = [];
  vademecumsOrdenComercio: VademecumOrdenComercio[] = [];
  img_receta?: File;
  preview_img_receta?: string;

  constructor(private http: HttpClient, private router: Router) {
    this.llenarDatos();
  }

  private llenarDatos(){
    this.getEstadosOrdenes(true).subscribe({
      next: (res) =>  {
        this.estadosOrdenes = res.map(eo => ({
          ...eo,
          b_activo: !eo.b_activo // convierte 1 → true y 0 → false
        }));
      },
      error: (e) => console.log("Error al obtener los Estados Ordenes Activo"),
      complete: () => console.log("Estados Ordenes Activos completado"),
    });
    this.getVademecums(true).subscribe({
      next: (res) =>  {
        this.vademecums = res.map(va => ({
          ...va,
          b_activo: !va.b_activo // convierte 1 → true y 0 → false
        }));
      },
      error: (e) => console.log("Error al obtener los Vademecums Activo"),
      complete: () => console.log("Vademecums Activos completado"),
    });
  }

  getUbicacionesComercioCercanas(n_latitud: number, n_longitud: number, n_distanciaKM: number): Observable<Ubicacion[]>{
    return this.http.get<Ubicacion[]>(`${this.API_URI}/ubicaciones/cercanas/${n_latitud}/${n_longitud}/${n_distanciaKM}`);
  }

  getComerciosCercanos(n_latitud: number, n_longitud: number, n_distanciaKM: number): Observable<Comercio[]>{
    return this.http.get<Comercio[]>(`${this.API_URI}/comercios/cercanos/${n_latitud}/${n_longitud}/${n_distanciaKM}`);
  }

  getVademecums(b_activo?: boolean): Observable<Vademecum[]>{
    const url = b_activo === undefined
      ? `${this.API_URI}/vademecums/buscar`
      : `${this.API_URI}/vademecums/buscar/${Number(b_activo)}`; // Comillas invertidas ALT + 96

    return this.http.get<Vademecum[]>(url);
  }

  getEstadosOrdenes(b_activo?: boolean): Observable<EstadoOrden[]>{
    const url = b_activo === undefined
      ? `${this.API_URI}/estadosOrdenes/buscar`
      : `${this.API_URI}/estadosOrdenes/buscar/${Number(b_activo)}`; // Comillas invertidas ALT + 96

    return this.http.get<EstadoOrden[]>(url);
  }

  getMaxProcentajeDescuentoComercio(id_comercio: string): Observable<number>{
    return this.http.get<number>(`${this.API_URI}/comercios/maxDiscountRate/${id_comercio}`);
  }

  getOrdenComercio(id: number): Observable<OrdenComercio | null>{
    return this.http
      .get<OrdenComercio>(`${this.API_URI}/ordenesComercio/xId/${id}`)
      .pipe(
        catchError((err) => {
          if (err?.status === 404) return of(null);   // no existe calificación => ok
          return throwError(() => err);               // cualquier otro error => que explote
        })
      );
  }

  getProductosXOrdenComercio(id_orden_comercio: number): Observable<ProductoOrdenComercio[]>{
    return this.http.get<ProductoOrdenComercio[]>(`${this.API_URI}/productos/xOrdenComercio/${id_orden_comercio}`);
  }

  getVademecumsXOrdenComercio(id_orden_comercio: number): Observable<VademecumOrdenComercio[]>{
    return this.http.get<VademecumOrdenComercio[]>(`${this.API_URI}/vademecums/xOrdenComercio/${id_orden_comercio}`);
  }

  getOrdenesComercioXUsuario(id_usuario: string): Observable<OrdenComercio[]>{
    return this.http.get<OrdenComercio[]>(`${this.API_URI}/ordenesComercio/xUsuario/${id_usuario}`);
  }

  getOrdenesComercioXComercio(id_comercio: string): Observable<OrdenComercio[]>{
    return this.http.get<OrdenComercio[]>(`${this.API_URI}/ordenesComercio/xComercio/${id_comercio}`);
  }

  getCalificacionesOrdenComercioXUsuario(id_usuario: string): Observable<CalificacionOrdenComercio[]>{
    return this.http.get<CalificacionOrdenComercio[]>(`${this.API_URI}/calificacionesOrdenesComercio/xUsuario/${id_usuario}`);
  }

  getCalificacionesOrdenComercioXComercio(id_comercio: string): Observable<CalificacionOrdenComercio[]>{
    return this.http.get<CalificacionOrdenComercio[]>(`${this.API_URI}/calificacionesOrdenesComercio/xComercio/${id_comercio}`);
  }

  getCalificacionOrdenComercioXOrdenComercio(id_orden_comercio: number): Observable<CalificacionOrdenComercio | null>{
    return this.http
      .get<CalificacionOrdenComercio>(`${this.API_URI}/calificacionesOrdenesComercio/xOrdenComercio/${id_orden_comercio}`)
      .pipe(
        catchError((err) => {
          if (err?.status === 404) return of(null);   // no existe calificación => ok
          return throwError(() => err);               // cualquier otro error => que explote
        })
      );
  }

  private createOrdenComercio(): Observable<any> {
    // NO tocar this.ordenComercio
    const orden = structuredClone
      ? structuredClone(this.ordenComercio)
      : JSON.parse(JSON.stringify(this.ordenComercio));

    delete orden.d_date_create;
    delete orden.d_date_updated;
    delete orden.estadoOrden;
    delete orden.imgAvatarCliente;
    delete orden.imgAvatarComercio;
    delete orden.nombreCliente;
    delete orden.nombreComercio;
    delete orden.direccionComercio;

    const data = {
      ordenComercio: orden,
      productosOrdenComercio: this.productosOrdenComercio,
      vademecumsOrdenComercio: this.vademecumsOrdenComercio,
    };

    return this.http.post<any>(`${this.API_URI}/ordenesComercio`, data);
  }

  /*private createProductosOrdenComercio(id_orden_comercio: number): Observable<any>{
    if(this.productosOrdenComercio.length > 0){
      this.productosOrdenComercio.forEach(producto => producto.id_orden_comercio = id_orden_comercio);
      return this.http.post<any>(`${this.API_URI}/ordenesComercio/productos`, this.productosOrdenComercio);
    }else{
      return new Observable((observable) => observable.next());
    }
  }

  private createVademecumsOrdenComercio(id_orden_comercio: number): Observable<any>{
    if(this.vademecumsOrdenComercio.length > 0){
      this.vademecumsOrdenComercio.forEach(vademecum => {
        vademecum.id_orden_comercio = id_orden_comercio;
        delete vademecum.nombreLaboratorio;
      });
      return this.http.post<any>(`${this.API_URI}/ordenesComercio/vademecums`, this.vademecumsOrdenComercio);
    }else{
      return new Observable((observable) => observable.next());
    }
  }*/

  createCalificacionOrdenComercio(calificacionOrdenComercio: CalificacionOrdenComercio): Observable<any>{
    return this.http.post<any>(`${this.API_URI}/calificacionesOrdenesComercio`, calificacionOrdenComercio);
  }

  updateOrdenComercio(ordenComercio: OrdenComercio, vademecumsOrdenComercio: VademecumOrdenComercio[], productosOrdenComercio: ProductoOrdenComercio[]): Observable<void>{
    delete ordenComercio.d_date_create;
    delete ordenComercio.d_date_updated;
    delete ordenComercio.estadoOrden;
    delete ordenComercio.imgAvatarCliente;
    delete ordenComercio.imgAvatarComercio;
    delete ordenComercio.nombreCliente;
    delete ordenComercio.nombreComercio;
    delete ordenComercio.direccionComercio;

    const data = {
      ordenComercio: ordenComercio,
      productosOrdenComercio: productosOrdenComercio,
      vademecumsOrdenComercio: vademecumsOrdenComercio
    };
    return this.http.put<void>(`${this.API_URI}/ordenesComercio`, data);
  }

  // Listado paginado y buscable para el admin (pantalla "Órdenes de Comercio" en More).
  buscarAdmin(params: { page: number; limit: number; search?: string; id_estado_orden?: number | null }): Observable<OrdenComercioAdminResponse> {
    let httpParams = new HttpParams().set('page', params.page).set('limit', params.limit);
    if (params.search) httpParams = httpParams.set('search', params.search);
    if (params.id_estado_orden) httpParams = httpParams.set('id_estado_orden', params.id_estado_orden);
    return this.http.get<OrdenComercioAdminResponse>(`${this.API_URI}/ordenesComercio/admin`, { params: httpParams });
  }

  updateEstadoOrdenComercio(id_orden_comercio: number, id_estado_orden: number): Observable<void>{
    let estadoOrdenComercio = {
      id_orden_comercio: id_orden_comercio,
      id_estado_orden: id_estado_orden
    }
    return this.http.put<void>(`${this.API_URI}/ordenesComercio/estadoOrdenComercio`, estadoOrdenComercio);
  }

  /*updateProductosOrdenComercio(productosOrdenComercio: ProductoOrdenComercio[]): Observable<void>{
    if(productosOrdenComercio.length > 0){
      return this.http.put<void>(`${this.API_URI}/ordenesComercio/productos`, productosOrdenComercio);
    }else{
      return new Observable((observable) => observable.next());
    }
  }

  updateVademecumsOrdenComercio(vademecumsOrdenComercio: VademecumOrdenComercio[]): Observable<void>{
    if(vademecumsOrdenComercio.length > 0){
      vademecumsOrdenComercio.forEach(vademecum => delete vademecum.nombreLaboratorio);
      return this.http.put<void>(`${this.API_URI}/ordenesComercio/vademecums`, vademecumsOrdenComercio);
    }else{
      return new Observable((observable) => observable.next());
    }
  }*/

  private updateOrderComercioImgReceta(x_img_receta: File, id_orden_comercio: number): Observable<any> {
    const formularioDeDatos = new FormData();
    formularioDeDatos.append('img_receta', x_img_receta);
    return this.http.post(`${this.API_URI}/ordenesComercio/receta/${id_orden_comercio}`, formularioDeDatos);
  }

  finalizeOrdenComercio(): Promise<any> {
    // 1) creo orden
    // 2) si hay receta: subo receta
    // 3) limpio data
    return firstValueFrom(
      this.createOrdenComercio().pipe(
        switchMap((resCoC: any) => {
          if (!this.img_receta) return of(resCoC);

          return this.updateOrderComercioImgReceta(this.img_receta, resCoC.id).pipe(
            // map(resUoCiR => resUoCiR)
            // ignoro la respuesta del upload y retorno la orden creada
            map(() => resCoC)
          );
        })
      )
    ).then((res) => {
      this.clearData();
      return res;
    }).catch((e) => {
      console.error(e);
      // mantené tus mensajes (para UI)
      throw new Error(this.img_receta ? 'Error al agregar la Receta de la Orden de Comercio' : 'Error al agregar la Orden de Comercio');
    });
  }

  /* POR AHORA NO SE USA PORQUE EL UPDATE DE ORDEN DE COMERCIO EN EL BACKEND ACTUALIZA LOS PRODUVTOS Y VADEMECUMS, SI SE LLEGA A NECESITAR HACERLO ACA SE PUEDE USAR ESTA, SINO SE USA EL UPDATEORDENCOMERCIO
  updateOrdenComercioVademecumsProductos(ordenComercio: OrdenComercio, productosOrdenComercio: ProductoOrdenComercio[], vademecumsOrdenComercio: VademecumOrdenComercio[]): Promise<any> {
    return new Promise((resolved, reject) => {
      this.updateOrdenComercio(ordenComercio, vademecumsOrdenComercio, productosOrdenComercio).subscribe({
        next: (resUoC) => {
          this.updateProductosOrdenComercio(productosOrdenComercio).subscribe({
            next: (resUPoC) => {
              this.updateVademecumsOrdenComercio(vademecumsOrdenComercio).subscribe({
                next: (resUVoC) => {
                  this.clearData();
                  resolved(resUoC);
                },
                error: (eUVoC) => {
                  reject('Error al actualizar los Vademecums de la Orden de Comercio');
                  console.error(eUVoC);
                },
                complete: () => console.log("Actualizar Vademecums Orden de Comercio completada")
              });
            },
            error: (eUPoC) => {
              reject('Error al actualizar los Productos de la Orden de Comercio');
              console.error(eUPoC);
            },
            complete: () => console.log("Actualizar Productos Orden de Comercio completada")
          });
        },
        error: (eUoC) => {
          reject('Error al actualizar la Orden de Comercio');
          console.error(eUoC);
        },
        complete: () => console.log("Actualizar Orden de Comercio completada"),
      });
    });
  }*/

  pagarMercadoPago(ordenComercio: OrdenComercio, vademecumsOrdenComercio: VademecumOrdenComercio[], productosOrdenComercio: ProductoOrdenComercio[]): Observable<any>{
    const data = {
      ordenComercio: ordenComercio,
      productosOrdenComercio: productosOrdenComercio,
      vademecumsOrdenComercio: vademecumsOrdenComercio
    };
    return this.http.put<any>(`${this.API_URI}/mercadoPago/createPreferencesComercio`, data);
  }

  // helper para limpiar
  clearReceta(): void {
    this.img_receta = undefined;
    this.preview_img_receta = undefined;
  }

  clearData(){
    const ordenComercio: OrdenComercio = {
      id: null,
      id_cliente: null,
      id_comercio: null,
      n_latitud: null,
      n_longitud: null,
      x_zona: null,
      x_img_receta: null,
      b_retiro_local: false,
      b_envio_domicilio: false,
      x_envio_direccion: '',
      b_pago_efectivo: false,
      b_pago_mercadopago: false
    };

    this.ordenComercio = ordenComercio;
    this.comercio = undefined;
    this.ubicacion = undefined;
    this.productosOrdenComercio = [];
    this.vademecumsOrdenComercio = [];
    this.img_receta = undefined;
    this.preview_img_receta = undefined;
  }
}
